<template>
  <div>
    <v-row justify="space-between">
      <v-col cols="12" xl="3" lg="3" md="3" sm="4" xs="12">
        <SearchText
          :text="'Search by name'"
          :value="'name'"
          @searchTextChanged="searchTextChanged"
          @keypress.enter.native.prevent="triggerSearch"
          @triggerSearch="triggerSearch"
          @triggerClear="triggerClear"
        ></SearchText>
      </v-col>
      <v-col cols="auto">
        <v-btn
          outlined
          class="black--text font-weight-bold mb-2"
          rounded
          large
          @click="downloadApplicants"
        >
          <v-icon color="black" small class="mr-2">
            mdi-download
          </v-icon>
          Download
        </v-btn>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-data-table :headers="headers" class="mt-5" :items="items" id="campaign-applicant-values">
      <template v-slot:item.actions="{ item }">
        <v-layout>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="(itemToBeEdited = item), (editDialog = true)"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-pencil-outline
                </v-icon>
              </v-btn>
            </template>
            {{ $t('edit') }}
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="(itemForPreview = item), (previewDialog = true)"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-eye
                </v-icon>
              </v-btn>
              <PreviewApplicantDialog
                :dialog="previewDialog"
                :applicant="itemForPreview"
                @close="previewDialog = false"
              ></PreviewApplicantDialog>
            </template>
            {{ $t('Preview') }}
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="(itemToBeDeleted = item), (deleteDialog = true)"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-delete-outline
                </v-icon>
              </v-btn>
            </template>
            Delete
          </v-tooltip>
        </v-layout>
      </template>
      <template v-slot:item.lastName="{ item }">
        <div>
          {{ item.lastname }}
        </div>
      </template>
      <template v-slot:item.firstName="{ item }">
        <div>{{ item.firstname }}</div>
      </template>
      <template v-slot:item.campaignId="{ item }">
        <div>
          {{ item.campaignId }}
        </div>
      </template>
      <template v-slot:item.title="{ item }">
        <div>{{ item.campaignTitle }}</div>
      </template>
      <template v-slot:item.email="{ item }">
        <div>
          {{ item.email }}
        </div>
      </template>
      <template v-slot:item.institution="{ item }">
        <div>
          {{ item.institution }}
        </div>
      </template>
      <template v-slot:item.position="{ item }">
        <div>
          {{ item.position }}
        </div>
      </template>
      <template v-slot:item.status="{ item }">
        <div>
          {{ item.status.replace('_', ' ') }}
        </div>
      </template>
      <template v-slot:item.date="{ item }">
        <div>
          {{ formatDateString(item.dateApplied) }}
        </div>
      </template>
    </v-data-table>

    <!-- Dialog for Delete Applicant -->
    <v-dialog v-if="itemToBeDeleted !== null" v-model="deleteDialog" max-width="675px">
      <v-card class="py-2 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title>
          {{ itemToBeDeleted.firstname }} {{ itemToBeDeleted.lastname }}
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete this applicant from the campaign
          {{ itemToBeDeleted.campaignTitle }}?
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%">
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="neutral800--text font-weight-bold"
                color="neutral"
                outlined
                @click="deleteDialog = false"
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="red darken-1"
                @click="onDeleteItem(itemToBeDeleted.id)"
              >
                {{ $t('delete.message') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog v-if="itemToBeEdited !== null" v-model="editDialog" max-width="675px">
      <v-card class="py-2 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title>
          {{ itemToBeEdited.campaignTitle }}
        </v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="selectedStatus"
            :items="statusOptions"
            item-value="value"
            item-text="text"
            class="pt-2"
            :placeholder="'Status'"
            outlined
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%;">
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="custom-button"
                outlined
                @click="cancelStatus"
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="custom-button"
                outlined
                @click="saveStatus"
                >{{ $t('save') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import SearchText from '@/components/shared/search/SearchText';
import PreviewApplicantDialog from './PreviewApplicantDialog.vue';

export default {
  name: 'TableCampaignApplicants',
  components: {
    SearchText,
    PreviewApplicantDialog,
  },
  props: {
    urlApi: String,
    search: Object,
    searchAttributes: Array,
  },
  data: () => ({
    headers: [
      { text: '', value: 'actions', sortable: false },
      { text: 'First Name', value: 'firstName' },
      { text: 'Last Name', value: 'lastName' },
      { text: 'Campaign ID', value: 'campaignId' },
      { text: 'Title', value: 'title' },
      { text: 'Email', value: 'email' },
      { text: 'Institution', value: 'institution' },
      { text: 'Position', value: 'position' },
      { text: 'Status', value: 'status' },
      { text: 'Date Applied', value: 'date' },
    ],
    editDialog: false,
    deleteDialog: false,
    itemToBeDeleted: null,
    itemToBeEdited: null,
    previewDialog: false,
    itemForPreview: null,
    items: [],
    totalItems: 0,
    firstLoad: true,
    options: {},
    selectedStatus: null,
    statusOptions: [
      { value: 'APPROVED', text: 'Approved' },
      { value: 'REJECTED', text: 'Rejected' },
      { value: 'UNDER_EVALUATION', text: 'Under Evaluation' },
    ],
  }),
  watch: {
    options: {
      handler() {
        console.log('[Table] Options watch');
        if (!this.firstLoad) {
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        }
      },
      deep: true,
    },
  },
  mounted() {
    console.log('[Table] Mounted');
    if (this.firstLoad) {
      /* Populate table from route */
      this.options.page = this.$route.query.index ? Number(this.$route.query.index) + 1 : 1;
      this.options.itemsPerPage = this.$route.query.size ? Number(this.$route.query.size) : 10;
      this.options.sortBy = [];
      this.options.sortDesc = [];
      if (this.$route.query.sort) {
        const sortList = this.$route.query.sort.split(',');
        for (let i = 0; i < sortList.length; i += 1) {
          this.options.sortBy.push(sortList[i].split(' ')[0]);
          if (sortList[i].split(' ')[1] === 'asc') {
            this.options.sortDesc.push(false);
          } else {
            this.options.sortDesc.push(true);
          }
        }
      }
      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.$route.query[attr]) {
            this.$set(this.search, attr, this.$route.query[attr]);
          }
        }
      }
    }
    this.getDataFromApi().then(data => {
      this.items = data.items;
      this.totalItems = data.total;
      this.firstLoad = false;
    });
  },
  methods: {
    searchTextChanged(obj) {
      console.log('[Table] searchTextChanged');
      this.search[obj.value] = obj.search;
      console.log(this.search);
    },
    triggerSearch() {
      console.log('[Table] Search watch');
      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    triggerClear() {
      console.log('[Table] Clear watch');

      this.search = {};

      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    cancelStatus() {
      this.selectedStatus = null;
      this.editDialog = false;
    },
    showPreviewDialog(item) {
      this.itemForPreview = item;
      this.previewDialog = true;
    },
    saveStatus() {
      EventBus.$emit('waiting', true);

      if (this.itemToBeEdited.campaignId) {
        const payload = {
          status: this.selectedStatus,
          id: this.itemToBeEdited.id,
        };

        this.axios
          .put(`api/v1/campaign/${this.itemToBeEdited.campaignId}/campaign-applicant`, payload)
          .then(response => {
            console.log('Request successful:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Status changed successfully!',
            });
            this.editDialog = false;
            this.getDataFromApi().then(data => {
              this.items = data.items;
              this.totalItems = data.total;
            });
          })
          .catch(error => {
            console.error('Request failed:', error);
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      }
    },

    getDataFromApi() {
      console.log('[Table] Loading data');
      this.loading = true;
      EventBus.$emit('waiting', true);
      return new Promise(resolve => {
        this.axios
          .get(`${this.urlApi}?`, {
            params: this.getParams(),
          })
          .then(res => {
            let items = [];
            let total = 0;
            if (res.status === 200) {
              items = res.data.content;
              total = res.data.total;
            }
            setTimeout(() => {
              this.loading = false;
              resolve({
                items,
                total,
              });
            });
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      });
    },
    getParams() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      const query = {
        index: page - 1,
        size: itemsPerPage,
      };
      let sort = '';
      for (let i = 0; i < sortBy.length; i += 1) {
        if (i !== 0) {
          sort += ',';
        }
        let dir = 'asc';
        if (sortDesc[i]) {
          dir = 'desc';
        }
        sort += `${sortBy[i]} ${dir}`;
      }
      if (sort !== '') {
        query.sort = sort;
      }
      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.search[attr]) {
            query[attr] = this.search[attr];
          }
        }
      }
      return query;
    },
    formatDateString(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    },
    onDeleteItem() {
      if (this.itemToBeDeleted.id && this.itemToBeDeleted.campaignId) {
        const id = this.itemToBeDeleted.id;
        const campaignId = this.itemToBeDeleted.campaignId;

        this.axios
          .put(`api/v1/campaign/${campaignId}/campaign-applicant/${id}`)
          .then(response => {
            console.log('Applicant deleted successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Deleted Successfully',
            });
            this.deleteDialog = false;
            this.getDataFromApi().then(data => {
              this.items = data.items;
              this.totalItems = data.total;
            });
          })
          .catch(error => {
            console.error('Error deleting applicant:', error);
          });
      } else {
        console.error('Applicant to be deleted is missing required fields.');
      }
    },
    downloadApplicants() {
      this.axios
        .get('api/v1/campaign/campaign-applicants/download', {
          responseType: 'blob',
        })
        .then(response => {
          const contentDisposition = response.headers['content-disposition'];
          let fileName = 'campaignApplicants.xlsx';
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="([^"]*)"/);
            if (fileNameMatch) {
              fileName = fileNameMatch[1];
            }
          }

          const url = window.URL.createObjectURL(new Blob([response.data]));

          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);

          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(error => {
          console.error('Error downloading file:', error);
        });
    },
  },
};
</script>

<style scoped>
.custom-button {
  color: black;
  font-weight: bold;
  margin: 0 2px;
}

.custom-button:hover {
  background: rgba(157, 87, 244, 1);
  color: white;
  font-weight: bold;
}
</style>
